export interface RgbColor {
    r: number;
    g: number;
    b: number;
}

export enum GaugeColors {
     GREEN = "rgba(105, 255, 79, 0.7019607843137254)",
     ORANGE = "rgba(255, 153, 38, 0.7019607843137254)",
     RED = "rgba(233, 34, 19, 0.7019607843137254)",
}

export class ColorUtils {

    public static hexToRgb(hex): RgbColor {
        const rgb: RgbColor = {r: 0, g: 0, b: 0};
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (result) {
            rgb.r = parseInt(result[1], 16);
            rgb.g = parseInt(result[2], 16);
            rgb.b = parseInt(result[3], 16);
            return rgb;
        }
        return null;
    }

    public static rgbToHex(rgb: RgbColor): string {
        return "#" + ((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b).toString(16).slice(1).toUpperCase();
    }

    public palette: Map<string, string[]>;
    public mappedColors: any;
    public colorsUsed: string[];

    /*
     * Give a ColorUtils instance giving a palette of colors for reference
     */
    constructor(palette: any) {
        this.palette = palette;
    }

    /**
     * Given a guid retrieve the cached value for it
     * get a new one from the palette of one color random if all
     * colors of the palette are all already mapped
     *
     */
    public getMappedColorShade(categoryType, guid: string, shadeIndex: number, numberOfShades: number) {

        if (this.mappedColors == null) {
            this.mappedColors = {};
            this.colorsUsed = [];
        }

        let color;
        if (this.mappedColors[guid] == null) {

            // find colors not yet used on the palette
            const remainsCustomColors = this.palette[categoryType]
                .filter((c) => {
                    return this.colorsUsed.indexOf(c.toUpperCase()) === -1;
                });

            // if all colors of the palette are used generate a random color and use it
            color = (remainsCustomColors.length !== 0 ?
                remainsCustomColors[0] :
                "#" + Math.floor(Math.random() * 16777215).toString(16).toUpperCase());
            this.colorsUsed.push(color.toUpperCase());

            // bright the color using computing the quantity of the brightining the remaining space color
            // using the numberOfShades given
            if (numberOfShades !== 0 && shadeIndex > 0) {
                let rgbColor = ColorUtils.hexToRgb(color);
                const shadeSize = Math.floor(
                    (255 - Math.max(rgbColor.r, rgbColor.g, rgbColor.b)) / Math.max(2, numberOfShades),
                );
                rgbColor = this.getBrighter(rgbColor, shadeSize * shadeIndex);
                color = ColorUtils.rgbToHex(rgbColor);
            }
            this.mappedColors[guid] = color;
        } else {
            if (shadeIndex !== 0) {
                let rgbColor = ColorUtils.hexToRgb(this.mappedColors[guid]);
                const shadeSize = Math.floor(
                    (255 - Math.max(rgbColor.r, rgbColor.g, rgbColor.b)) / Math.max(2, numberOfShades),
                );
                rgbColor = this.getBrighter(rgbColor, shadeSize * shadeIndex);
                return ColorUtils.rgbToHex(rgbColor).toUpperCase();
            }
        }

        return this.mappedColors[guid];
    }

    /**
     * Given a color return the shadeIndex-th shade of it
     *
     */
    public getColorShade(baseColor: string, shadeIndex: number, numberOfShades: number): string {

        // bright the color using computing the quantity of the brightining the remaining space color
        // using the numberOfShades given
        let newColor;
        if (numberOfShades !== 0 && shadeIndex > 0) {
            let rgbColor = ColorUtils.hexToRgb(baseColor);
            const shadeSize =
                Math.floor((255 - Math.max(rgbColor.r, rgbColor.g, rgbColor.b)) / Math.max(2, numberOfShades));
            rgbColor = numberOfShades === 1 ? rgbColor : this.getBrighter(rgbColor, shadeSize * shadeIndex);
            newColor = ColorUtils.rgbToHex(rgbColor);
        }

        return newColor || baseColor;
    }

    public getMappedColor(categoryType, guid: string) {
        return this.getMappedColorShade(categoryType, guid, 0, 0);
    }

    public getBrighter(color: RgbColor, quantity: number) {
        return {
            r: color.r + quantity,
            g: color.g + quantity,
            b: color.b + quantity,
        };
    }

    public getDarker(color: RgbColor, quantity: number) {
        return {
            r: color.r - quantity,
            g: color.g - quantity,
            b: color.b - quantity,
        };
    }

}
